import { type Accessor, createEffect, createSignal, onCleanup, onMount } from "solid-js";

/**
 * Arms a CSS-animated scene and tracks whether it is on screen. The scene's
 * keyframes only apply once it is armed, and run only while it is in view, so
 * the server render, a visitor without JavaScript and a reduced-motion visitor
 * all see the finished frame.
 */
export const useScene = () => {
  const [armed, setArmed] = createSignal(false);
  const [inView, setInView] = createSignal(false);
  let element: Element | undefined;

  onMount(() => {
    if (!element || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    setArmed(true);
    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), { threshold: 0.3 });
    observer.observe(element);
    onCleanup(() => observer.disconnect());
  });

  return {
    armed,
    inView,
    ref: (node: Element) => {
      element = node;
    },
  };
};

export type Scene = ReturnType<typeof useScene>;

/**
 * Counts beats of `stepMs`, counting only scene time spent on screen and not
 * `paused`; callers take the count modulo their own cycle. Unarmed, it stays on
 * beat 0. `jump` restarts the clock at a beat, so a visitor who stopped the
 * loop on one beat sees it carry on from the next.
 */
export const useBeat = (scene: Scene, stepMs: number, paused: Accessor<boolean>) => {
  const TICK = 100;
  const [beat, setBeat] = createSignal(0);
  let elapsed = 0;

  onMount(() => {
    if (!scene.armed()) return;
    const timer = window.setInterval(() => {
      if (!scene.inView() || paused()) return;
      elapsed += TICK;
      if (elapsed < stepMs) return;
      elapsed = 0;
      setBeat(beat() + 1);
    }, TICK);
    onCleanup(() => window.clearInterval(timer));
  });

  return {
    beat,
    jump: (next: number) => {
      elapsed = 0;
      setBeat(next);
    },
  };
};

/**
 * Seconds the scene has been on screen, paused while it is scrolled away and
 * stopped once past `end`. Unarmed, the scene is already over.
 */
export const useSceneClock = (scene: Scene, end: number): Accessor<number> => {
  const TICK = 100;
  const [seconds, setSeconds] = createSignal(Number.POSITIVE_INFINITY);
  // Runs after the scene's own onMount, as long as useScene was called first.
  onMount(() => {
    if (!scene.armed()) return;
    setSeconds(0);
    const timer = window.setInterval(() => {
      if (seconds() > end) return window.clearInterval(timer);
      if (scene.inView()) setSeconds((s) => s + TICK / 1000);
    }, TICK);
    onCleanup(() => window.clearInterval(timer));
  });
  return seconds;
};

/**
 * Counts from zero up to `target`, easing out, the first time the scene comes
 * on screen. Unarmed, it is `target` from the start.
 */
export const useCountUp = (target: number, scene: Scene): Accessor<number> => {
  const DURATION = 1400;
  const [progress, setProgress] = createSignal(1);

  onMount(() => {
    if (!scene.armed()) return;
    setProgress(0);
    let frame = 0;
    createEffect(() => {
      if (!scene.inView() || frame) return;
      const start = performance.now();
      const tick = (now: number) => {
        const p = Math.min(1, (now - start) / DURATION);
        setProgress(p);
        if (p < 1) frame = requestAnimationFrame(tick);
      };
      frame = requestAnimationFrame(tick);
    });
    onCleanup(() => cancelAnimationFrame(frame));
  });

  return () => Math.round(target * (1 - (1 - progress()) ** 3));
};

/**
 * Types `text` out character by character, starting `delayMs` into the scene's
 * time on screen and pausing whenever it scrolls away. Unarmed, it is the whole
 * text at once.
 */
export const useTyping = (text: Accessor<string>, scene: Scene, delayMs: number): Accessor<string> => {
  const TICK = 50;
  const PER_CHAR = 60;
  const [elapsed, setElapsed] = createSignal(0);

  // Runs after the scene's own onMount, as long as useScene was called first.
  onMount(() => {
    if (!scene.armed()) return;
    const timer = window.setInterval(() => {
      if (!scene.inView()) return;
      if (elapsed() > delayMs + text().length * PER_CHAR) return window.clearInterval(timer);
      setElapsed(elapsed() + TICK);
    }, TICK);
    onCleanup(() => window.clearInterval(timer));
  });

  return () => {
    if (!scene.armed()) return text();
    return text().slice(0, Math.max(0, Math.floor((elapsed() - delayMs) / PER_CHAR)));
  };
};
